import { Injectable, Output, EventEmitter } from '@angular/core';
import { 
  ApiService, 
  ClientEventService, 
  ServerEventService, 
  MessageService 
} from '@radsource/ng-common-lib';

import { PatientInstance } from '../objects/patient-instance';
import { StudyInstance } from '../objects/study-instance';
import { SeriesInstance } from '../objects/series-instance';
import { SOPInstance } from '../objects/sop-instance';
import { of } from 'rxjs';
import { DicomService } from './dicom.service';
import { CMoveProgress } from '../objects/cmove-progress';

// import { NodeService } from './node.service';
// import { UserService } from './user.service';

@Injectable({
  providedIn: 'root',
})
export class InstanceService {

  renderPatients: Array<PatientInstance> = new Array<PatientInstance>();

  patients: Array<PatientInstance> = new Array<PatientInstance>();
  patients$: any = of(this.patients);

  masterPatients: Array<PatientInstance> = new Array<PatientInstance>();
  masterPatients$: any = of(this.masterPatients);

  masterStudies: Array<StudyInstance> = new Array<StudyInstance>();
  masterStudies$: any = of(this.masterStudies);

  masterSeries: Array<SeriesInstance> = new Array<SeriesInstance>();
  masterSeries$: any = of(this.masterSeries);

  masterSOPs: Array<SOPInstance> = new Array<SOPInstance>();
  masterSOPs$: any = of(this.masterSOPs);

  updatingMasterSOPs: Boolean = false;

  tempStudy: StudyInstance = new StudyInstance();

  //SIGNAL Objects
  patientEditing: PatientInstance = null;
  @Output() patientEditing$: EventEmitter<Boolean> = new EventEmitter<Boolean>();

  studyEditing: StudyInstance = null;
  @Output() studyEditing$: EventEmitter<Boolean> = new EventEmitter<Boolean>();

  seriesEditing: SeriesInstance = null;
  @Output() seriesEditing$: EventEmitter<Boolean> = new EventEmitter<Boolean>();

  @Output() cmoveArchiveDetail$: EventEmitter<any> = new EventEmitter<any>();
  @Output() cmoveArchiveSOPDetail$: EventEmitter<any> = new EventEmitter<any>();

  cmoveStudyDetail: StudyInstance = null;
  @Output() cmoveStudyDetail$: EventEmitter<StudyInstance> = new EventEmitter<StudyInstance>();

  cmoveSOPDetail: SOPInstance = null;
  @Output() cmoveSOPDetail$: EventEmitter<SOPInstance> = new EventEmitter<SOPInstance>();

  @Output() clearSOPDetails$: EventEmitter<any> = new EventEmitter<any>();
  @Output() clearStudyDetails$: EventEmitter<any> = new EventEmitter<any>();

  editingInstanceType: string = "";

  disableEditing: Boolean = false;
  @Output() disableEditing$: EventEmitter<Boolean> = new EventEmitter<Boolean>();

  tempThumbnails: any = [];

  @Output() patientsChanges: EventEmitter<any> = new EventEmitter();
  @Output() studiesChanges: EventEmitter<any> = new EventEmitter();
  @Output() seriesChanges: EventEmitter<any> = new EventEmitter();
  @Output() sopsChanges: EventEmitter<any> = new EventEmitter();

  constructor(
    private api: ApiService,
    private ces: ClientEventService,
    private ses: ServerEventService,
    private ms: MessageService,
    private ds: DicomService,
  ) { }

  editPatient(instance) {
    try {
      this.closeEdit();
      this.editingInstanceType = "PATIENT";
      this.patientEditing = new PatientInstance(instance);
      this.patientEditing$.emit(true);
    } catch (err) {
      this.ms.handleClientError(err);
    }
  }

  editStudy(instance) {
    try {
      this.closeEdit();
      this.editingInstanceType = "STUDY";
      this.studyEditing = new StudyInstance(instance);
      this.studyEditing$.emit(true);
    } catch (err) {
      this.ms.handleClientError(err);
    }
  }

  editSeries(instance) {
    try {
      this.closeEdit();
      this.editingInstanceType = "SERIES";
      this.seriesEditing = new SeriesInstance(instance);
      this.seriesEditing$.emit(true);
    } catch (err) {
      this.ms.handleClientError(err);
    }
  }

  clearEditing() {
    try {
      this.patients.forEach((p) => {
        p.editing = false;
        p.studies.forEach((st) => {
          st.editing = false;
          st.series.forEach((se) => {
            se.editing = false;
          });
        });
      });
    } catch (err) {
      this.ms.handleClientError(err);
    }
  }

  closeEdit() {
    try {
      this.editingInstanceType = "";
      this.patientEditing = null;
      this.patientEditing$.emit(false);
      this.studyEditing = null;
      this.studyEditing$.emit(false);
      this.seriesEditing = null;
      this.seriesEditing$.emit(false);
    } catch (err) {
      this.ms.handleClientError(err);
    }
  }

  clearAccessionNumbers() {
    return this.api.put("wado/studies/owner/clearaccnums/", {})
      .then((res: any) => {
        return res;
      }).catch((err: any) => {
        throw err;
      });
  }

  updateMasters() {

    try {
      this.masterPatients = new Array<PatientInstance>();
      this.masterStudies = new Array<StudyInstance>();
      this.masterSeries = new Array<SeriesInstance>();
      this.masterSOPs = new Array<SOPInstance>();

      return Promise.all([
        this.getPatientsAsOwner(),
        this.getStudiesAsOwner(),
        this.getSeriesAsOwner(),
        this.getSOPsAsOwner()
      ]).then((res) => {
        try {
          //update MasterPatients
          res[0].forEach(pat => {
            this.parsePatient(new PatientInstance(pat));
          });
          this.masterPatients$ = of(this.masterPatients);

          //update MasterStudies
          res[1].forEach(stu => {
            this.parseStudy(new StudyInstance(stu));
          });
          this.masterStudies$ = of(this.masterStudies);

          //update MasterSeries
          res[2].forEach(ser => {
            this.parseSeries(new SeriesInstance(ser));
          });
          this.masterSeries$ = of(this.masterSeries);

          //update MasterSOPs
          res[3].forEach(sop => {
            this.parseSOP(new SOPInstance(sop));
          });
          this.masterSOPs$ = of(this.masterSOPs);
        } catch (err) {
          this.ms.handleClientError(err);
        }
      }).catch((err) => {
        throw err;
      });
    } catch (err) {
      this.ms.handleClientError(err);
    }
  }

  //PATIENTS
  // private getPatients(): any {
  //   return this.api.get("wado/patients/");
  // } 

  // private deletePatients(): any {
  //   return this.api.delete("wado/patients/");
  // } 

  getPatientsAsOwner(): any {
    return this.api.get("wado/patients/owner/");
  }

  deletePatientsAsOwner(): any {
    return this.api.delete("wado/patients/owner/");
  }

  getPatientAsOwner(recordID: any): any {
    return this.api.get("wado/patients/owner/" + recordID + "/")
      .then((res: any) => {
        try {

          let inst = new PatientInstance(res.body);
          return inst;
        } catch (err) {
          this.ms.handleClientError(err);
        }
      }).catch((err: any) => {
        throw err;
      });
  }

  deletePatientAsOwner(patientInst: PatientInstance): any {
    return this.api.delete("wado/patients/owner/" + patientInst.record_id + "/")
      .then((res: any) => {
        try {
          this.closeEdit();
          let patient = this.patients.find((p) => p.record_id == patientInst.record_id);
          this.patients.splice(this.patients.indexOf(patient), 1);

          this.masterSOPs = this.masterSOPs.filter(sop => sop.patient.record_id != patient.record_id);
          this.masterSOPs$ = of(this.masterSOPs);

          this.masterSeries = this.masterSeries.filter(ser => ser.patient.record_id != patient.record_id);
          this.masterSeries$ = of(this.masterSeries);

          this.masterStudies = this.masterStudies.filter(stu => stu.patient.record_id != patient.record_id);
          this.masterStudies$ = of(this.masterStudies);

          this.masterPatients = this.masterPatients.filter(pat => pat.record_id != patient.record_id);
          this.masterPatients$ = of(this.masterPatients);

          return res;
        } catch (err) {
          this.ms.handleClientError(err);
        }
      }).catch((err: any) => {
        throw err;
      });
  }

  updatePatientAsOwner(patientInstance: PatientInstance) {
    let patient;
    let body;
    try {

      patient = this.patients.find((p) => patientInstance.record_id == p.record_id);
      if (patient) {
        patient.editing = false;
        patient.saving = true;
        this.ces.savingPatientInstance.emit(patient);
      }

      body = new PatientInstance(patientInstance).essentials();
    } catch (err) {
      this.ms.handleClientError(err);
    }

    return this.api.put("wado/patients/owner/" + patientInstance.record_id + "/", body)
      .then((res: any) => {
        try {

          let body = res;
          patient.saving = false;
          patient.update(body);

          // this.updateMasters();

          this.patients.sort((a: any, b: any) => {
            if (a.name < b.name) return -1;
            if (a.name > b.name) return 1;
            return 0;
          });
          this.closeEdit();
          return res;
        } catch (err) {
          this.ms.handleClientError(err);
        }
      }).catch((err: any) => {
        throw err;
      });
  }

  parsePatient(
    patientInst: PatientInstance
  ): any {
    try {
      let patient = this.masterPatients.find((p) => p.record_id == patientInst.record_id)
      if (patient) {
        patient.update(patientInst);
      } else {
        this.masterPatients.push(patientInst);
      }
      //sort patients
      this.masterPatients.sort((a: any, b: any) => {
        if (a.name < b.name) return -1;
        if (a.name > b.name) return 1;
        return 0;
      });
      this.masterPatients$ = of(this.masterPatients);
    } catch (err) {
      this.ms.handleClientError(err);
    }
  }


  //STUDIES

  getStudiesAsOwner(): any {
    return this.api.get("wado/studies/owner/");
  }

  getStudyAsOwner(recordID: any): any {
    return this.api.get("wado/studies/owner/" + recordID + "/")
      .then((res: any) => {
        let inst = new StudyInstance(res.body);
        return inst;
      }).catch((err: any) => {
        throw err;
      });
  }

  deleteStudyAsOwner(studyInst: StudyInstance): any {
    return this.api.delete("wado/studies/owner/" + studyInst.record_id + "/")
      .then((res: any) => {
        try {
          this.closeEdit();
          let patient = this.patients.find((p) => p.record_id == studyInst.patient_record_id);
          let study = patient.studies.find((s) => s.record_id == studyInst.record_id);
          patient.studies.splice(patient.studies.indexOf(study), 1);

          this.masterSOPs = this.masterSOPs.filter(sop => sop.study.record_id != study.record_id);
          this.masterSOPs$ = of(this.masterSOPs);

          this.masterSeries = this.masterSeries.filter(ser => ser.study.record_id != study.record_id);
          this.masterSeries$ = of(this.masterSeries);

          this.masterStudies = this.masterStudies.filter(stu => stu.record_id != study.record_id);
          this.masterStudies$ = of(this.masterStudies);

          if (patient.studies.length == 0) {
            this.patients.splice(this.patients.indexOf(patient), 1);

            this.masterPatients = this.masterPatients.filter(pat => pat.record_id != patient.record_id);
            this.masterPatients$ = of(this.masterPatients);
          }
          return res;
        } catch (err) {
          this.ms.handleClientError(err);
        }
      }).catch((err: any) => {
        throw err;
      });
  }

  updateStudyAsOwner(studyInstance: StudyInstance) {
    let body;
    try {
      let patient = this.patients.find((p) => studyInstance.patient_record_id == p.record_id);
      let study = patient.studies.find((s) => studyInstance.record_id == s.record_id);
      study.editing = false;
      study.saving = true;

      body = new StudyInstance(studyInstance).essentials();
      body['owner'] = this.api.user();
    } catch (err) {
      this.ms.handleClientError(err);
    }

    return this.api.put("wado/studies/owner/" + studyInstance.record_id + "/", body)
      .then((res: any) => {
        try {

          let body = res;
          let patient = this.patients.find((p) => body.patient_record_id == p.record_id);
          let study = patient.studies.find((s) => body.record_id == s.record_id);
          study.saving = false;
          study.update(body);
          this.closeEdit();
          return res;
        } catch (err) {
          this.ms.handleClientError(err);
        }
      }).catch((err: any) => {
        throw err;
      });
  }

  parseStudy(
    studyInst: StudyInstance
  ): any {
    try {
      let study = this.masterStudies.find((s) => s.record_id == studyInst.record_id)
      if (study) {
        study.update(studyInst);
      } else {
        this.masterStudies.push(studyInst);
      }
      this.masterStudies.sort((a: any, b: any) => {
        if (a.date < b.date) return -1;
        if (a.date > b.date) return 1;
        return 0;
      });
      this.masterStudies$ = of(this.masterStudies);
    } catch (err) {
      this.ms.handleClientError(err);
    }
  }

  //SERIES

  getSeriesAsOwner(): any {
    return this.api.get("wado/series/owner/");
  }

  getOneSeriesAsOwner(recordID: any): any {
    return this.api.get("wado/series/owner/" + recordID + "/")
      .then((res: any) => {
        try {

          let inst = new SeriesInstance(res.body);
          return inst;
        } catch (err) {
          this.ms.handleClientError(err);
        }
      }).catch((err: any) => {
        throw err;
      });
  }

  getSeriesProgressAsOwner(requestID: any, recordID: any): any {
    return this.api.get("cmove/requests/owner/" + requestID + "/wadoseries/" + recordID + "/sops/")
      .then((res: any) => {
        return res[0];
      }).catch((err: any) => {
        throw err;
      });
  }

  deleteSeriesAsOwner(seriesInst: SeriesInstance): any {
    return this.api.delete("wado/series/owner/" + seriesInst.record_id + "/")
      .then((res: any) => {
        try {
          this.closeEdit();
          let patient;
          this.patients.forEach((p) => {
            if (p.studies.find((s) => s.record_id == seriesInst.study_record_id)) {
              patient = p;
            }
          });

          let study = patient.studies.find((s) => s.record_id == seriesInst.study_record_id);
          study.series.splice(study.series.indexOf(seriesInst), 1);

          this.masterSOPs = this.masterSOPs.filter(sop => sop.series.record_id != seriesInst.record_id);
          this.masterSOPs$ = of(this.masterSOPs);

          this.masterSeries = this.masterSeries.filter(ser => ser.record_id != seriesInst.record_id);
          this.masterSeries$ = of(this.masterSeries);

          //check number of series for study deletion
          if (study.series.length == 0) {
            patient.studies.splice(patient.studies.indexOf(study), 1);

            this.masterStudies = this.masterStudies.filter(stu => stu.record_id != study.record_id);
            this.masterStudies$ = of(this.masterStudies);
          } else {
            study.number_of_study_related_series = study.series.length;
            study.number_of_study_related_instances = study.series.length;
          }

          //check number of studies for patient deletion
          if (patient.studies.length == 0) {
            this.patients.splice(this.patients.indexOf(patient), 1);

            this.masterPatients = this.masterPatients.filter(pat => pat.record_id != patient.record_id);
            this.masterPatients$ = of(this.masterPatients);
          }

          return res;
        } catch (err) {
          this.ms.handleClientError(err);
        }
      }).catch((err: any) => {
        throw err;
      });
  }

  updateSeriesAsOwner(seriesInstance: SeriesInstance) {
    let patient;
    let study;
    let series;
    let body;

    try {
      patient = this.patients.find((p) => seriesInstance.patient.record_id == p.record_id);
      study = patient.studies.find((st) => seriesInstance.study.record_id == st.record_id);
      series = study.series.find((se) => seriesInstance.record_id == se.record_id);
      series.editing = false;
      series.saving = true;

      body = new SeriesInstance(seriesInstance).essentials();
      body['owner'] = this.api.user();

    } catch (err) {
      this.ms.handleClientError(err);
    }

    return this.api.put("wado/series/owner/" + seriesInstance.record_id + "/", body)
      .then((res: any) => {
        try {
          let body = res;
          series.saving = false;
          series.update(body);

          study.series.sort((a: any, b: any) => {
            if (a.number < b.number) return -1;
            if (a.number > b.number) return 1;
            return 0;
          });
          study.series$ = of(study.series);
          this.closeEdit();
          return res;
        } catch (err) {
          this.ms.handleClientError(err);
        }
      }).catch((err: any) => {
        throw err;
      });
  }

  parseSeries(
    seriesInst: SeriesInstance
  ): any {
    try {

      let series = this.masterSeries.find((s) => s.record_id == seriesInst.record_id);
      if (series) {
        series.update(seriesInst);
      } else {
        this.masterSeries.push(seriesInst);
      }
      this.masterSeries.sort((a: any, b: any) => {
        if (a.number < b.number) return -1;
        if (a.number > b.number) return 1;
        return 0;
      });
      this.masterSeries$ = of(this.masterSeries);
    } catch (err) {
      this.ms.handleClientError(err);
    }
  }

  //SOPS
  getSOPsAsOwner(): any {
    return this.api.get("wado/sops/owner/");
  }

  getSOPAsOwner(recordID: any): any {
    return this.api.get("wado/sops/owner/" + recordID + "/")
      .then((res: any) => {
        try {

          let inst = new SOPInstance(res.body);
          this.parseSOP(inst);
          return inst;
        } catch (err) {
          this.ms.handleClientError(err);
        }
      }).catch((err: any) => {
        throw err;
      });
  }

  applyPatientsProgress() {
    this.patients.forEach((p) => {
      p.studies$ = of(p.studies);
      p.updateProgress(this.ds.cMoveRequests.length > 0);
    });
  }

  applyStudiesProgress(data: any) {
    // let allStudies = this.patients.reduce((accumulator, currentValue) => {
    //   return [...accumulator, ...currentValue.studies];
    // }, []);

    this.tempStudy = new StudyInstance();

    this.tempStudy = this.masterStudies.find((study) => study.record_id == data.wado_study_record_id);
    this.tempStudy.updateProgress(new CMoveProgress(data));
    this.sortMasters();
  }

  applySeriesProgress() {

    let allStudies = this.patients.reduce((accumulator, currentValue) => {
      return [...accumulator, ...currentValue.studies];
    }, []);

    let allSeries = allStudies.reduce((accumulator, currentValue) => {
      return [...accumulator, ...currentValue.series];
    }, []);

    this.masterSeries.forEach((s: SeriesInstance) => {
      if (this.ds.cMoveRequests.length > 0) {
        s.updateProgress();
      } else {
        s.pushProgress = { state: "QUEUED" };
        s.pushProgress$ = of(s.pushProgress);
      }
    });

    allSeries.forEach((s: SeriesInstance) => {
      if (this.ds.cMoveRequests.length > 0) {
        s.updateProgress();
      } else {
        s.pushProgress = { state: "QUEUED" };
        s.pushProgress$ = of(s.pushProgress);
      }
    });

    this.masterSeries$ = of(this.masterSeries);
  }

  applySOPProgress(progressResponse): any {

    let allStudies = this.patients.reduce((accumulator, currentValue) => {
      return [...accumulator, ...currentValue.studies];
    }, []);

    let allSeries = allStudies.reduce((accumulator, currentValue) => {
      return [...accumulator, ...currentValue.series];
    }, []);

    let allSOPs = allSeries.reduce((accumulator, currentValue) => {
      return [...accumulator, ...currentValue.sops];
    }, []);

    this.masterSOPs.map(sop => {
      if (progressResponse.length) {
        sop.pushProgress = progressResponse.find(res => sop.record_id == res.wado_sop_record_id);
        sop.pushProgress$ = of(sop.pushProgress);
      } else {
        sop.pushProgress = { state: 'QUEUED' };
        sop.pushProgress$ = of(sop.pushProgress);
      }
    });

    this.masterSOPs$ = of(this.masterSOPs);

    allSOPs.map(sop => {
      if (progressResponse.length) {
        sop.pushProgress = progressResponse.find(res => sop.record_id == res.wado_sop_record_id);
        sop.pushProgress$ = of(sop.pushProgress);
      } else {
        sop.pushProgress = { state: 'QUEUED' };
        sop.pushProgress$ = of(sop.pushProgress);
      }
    });

    this.patients$ = of(this.patients);
  }

  getCMoveSummaryAsSuperUser(requestID: any): Promise<any> {
    return this.api.get("cmove/requests/super/" + requestID + "/summary/");
  }

  getCMoveSummaryAsAdmin(requestID: any, owner?: any): Promise<any> {
    return this.api.get("cmove/requests/admin/" + requestID + "/summary/", { owner: owner })
  }

  getCMoveSummaryAsOwner(requestID: any): Promise<any> {
    return this.api.get("cmove/requests/owner/" + requestID + "/summary/");
  }

  getCMoveRequestAsOwner(requestID: any): Promise<any> {
    return this.api.get("cmove/requests/owner/" + requestID + "/").then((res) => {
      if (res.length == 1) {
        return res[0];
      }
    }).catch((err) => {
      throw err;
    });
  }

  searchCMoveArchiveAsOwner(params: any): any {

    // params['category'] = this.ns.appNode.uid;
    //UPDATE THIS TO TAKE A UID - Genericize

    return this.api.get("cmove/archive/owner/", params);
  }

  searchCMoveArchive(params: any): any {

    // params['category'] = this.ns.appNode.uid;
    //UPDATE THIS TO TAKE A UID - Genericize

    return this.api.get("cmove/archive/", params);
  }

  getSOPInfoByRequestForOwner(requestID: any, owner: any): any {
    return this.api.get("cmove/requests/" + requestID + "/sops/", { owner: owner });
  }

  getSOPInfoByRequestAsOwner(requestID: any): any {
    return this.api.get("cmove/requests/owner/" + requestID + "/sops/");
  }

  getSOPProgressAsOwner(): any {
    return this.api.get("cmove/requests/owner/sops/");
  }

  parseSOP(
    sopInst: SOPInstance
  ): any {
    try {

      let sop = this.masterSOPs.find((s) => s.record_id == sopInst.record_id)
      if (sop) {
        sop.update(sopInst);
      } else {
        this.masterSOPs.push(sopInst);
      }
      this.masterSOPs.sort((a: any, b: any) => {
        if (a.instance_number < b.instance_number) return -1;
        if (a.instance_number > b.instance_number) return 1;
        return 0;
      });
      this.masterSOPs$ = of(this.masterSOPs);
    } catch (err) {
      this.ms.handleClientError(err);
    }
  }

  deleteSOPAsOwner(sopInst: SOPInstance): any {
    return this.api.delete("wado/sops/owner/" + sopInst.record_id + "/")
      .then((res: any) => {
        try {

          this.closeEdit();
          let patient;
          this.patients.forEach((p) => {
            if (p.studies.find((s) => s.record_id == sopInst.study.record_id)) {
              patient = p;
            }
          });

          if (!patient) {
            return res;
          }

          let study = patient.studies.find((s) => s.record_id == sopInst.study.record_id);
          let series = study.series.find((s) => s.record_id == sopInst.series_record_id);

          series.sops.splice(series.sops.indexOf(sopInst), 1);

          //check number of sops for series deletion
          if (series.sops.length == 0) {
            study.series.splice(study.series.indexOf(series), 1);
          } else {
            series.number_of_study_related_sops = series.sops.length;
            series.number_of_study_related_instances = series.sops.length;
          }

          this.masterSOPs = this.masterSOPs.filter(sop => {
            return sop.record_id != sopInst.record_id;
          });
          this.masterSOPs$ = of(this.masterSOPs);

          if (series.sops.length == 0) {
            this.masterSeries = this.masterSeries.filter(ser => ser.record_id != series.record_id);
            this.masterSeries$ = of(this.masterSeries);
          }

          //check number of series for study deletion
          if (study.series.length == 0) {
            patient.studies.splice(patient.studies.indexOf(study), 1);

            this.masterStudies = this.masterStudies.filter(stu => stu.record_id != study.record_id);
            this.masterStudies$ = of(this.masterStudies);
          } else {
            study.number_of_study_related_series = study.series.length;
            study.number_of_study_related_instances = study.series.length;
          }

          //check number of studies for patient deletion
          if (patient.studies.length == 0) {
            this.patients.splice(this.patients.indexOf(patient), 1);

            this.masterPatients = this.masterPatients.filter(pat => pat.record_id != patient.record_id);
            this.masterPatients$ = of(this.masterPatients);
          }

          return res;
        } catch (err) {
          this.ms.handleClientError(err);
        }
      }).catch((err: any) => {
        throw err;
      });
  }

  seriesThumbnailBatch() {
    let allStudies = this.patients.reduce((accumulator, currentValue) => {
      return [...accumulator, ...currentValue.studies];
    }, []);

    let allSeries = allStudies.reduce((accumulator, currentValue) => {
      return [...accumulator, ...currentValue.series];
    }, []);

    allSeries.forEach((s: SeriesInstance) => {
      this.retrieveSeriesThumbnail(s.record_id).then((res) => {
        let mSeries = this.masterSeries.find(se => se.record_id == s.record_id);
        mSeries.thumbnail = res;
        s.thumbnail = res;
      });
    });
  }

  sopThumbnailBatch() {

    let allStudies;
    let allSeries;
    let allSOPs;

    try {
      allStudies = this.patients.reduce((accumulator, currentValue) => {
        return [...accumulator, ...currentValue.studies];
      }, []);

      allSeries = allStudies.reduce((accumulator, currentValue) => {
        return [...accumulator, ...currentValue.series];
      }, []);

      allSOPs = allSeries.reduce((accumulator, currentValue) => {
        return [...accumulator, ...currentValue.sops];
      }, []);

    } catch (err) {
      this.ms.handleClientError(err);
    }

    allSOPs.forEach((s: SOPInstance) => {
      this.retrieveSOPThumbnailAsOwner(s.record_id).then((res) => {
        try {
          let mSOP = this.masterSOPs.find(se => se.record_id == s.record_id);
          mSOP.thumbnail = res;
          s.thumbnail = res;
        } catch (err) {
          this.ms.handleClientError(err);
        }
      });
    });
  }

  retrieveSeriesThumbnail(recordID: any, height?: any, width?: any) {

    let params = {};
    if (height != null) { params['height'] = height };
    if (width != null) { params['width'] = width };

    return this.api.get("wado/series/owner/" + recordID + "/thumbnail/", params)
      .then((res: any) => {
        return res;
      }).catch((err: any) => {
        throw err;
      });
  }

  retrieveSOPThumbnailAsOwner(recordID: any, height?: any, width?: any): any {

    let params = {};
    if (height) { params['height'] = height };
    if (width) { params['width'] = width };

    return this.api.get("wado/sops/owner/" + recordID + "/thumbnail/", params)
      .then((res: any) => {
        return res;
      }).catch((err: any) => {
        throw err;
      });
  }

  sortMasters(): any {
    try {
      //set up Masters connections
      this.masterStudies.forEach((st) => {
        st.patient = this.masterPatients.find((p) => p.record_id == st.patient_record_id);
      });

      this.masterSeries.forEach((se) => {
        se.study = this.masterStudies.find((st) => st.record_id == se.study_record_id);
        se.patient = se.study.patient;
      });

      this.masterSOPs.forEach((so) => {
        so.series = this.masterSeries.find((se) => se.record_id == so.series_record_id);
        so.study = so.series.study;
        so.patient = so.series.patient;
      });

      this.masterStudies$ = of(this.masterStudies);
      this.masterSeries$ = of(this.masterSeries);
      this.masterSOPs$ = of(this.masterSOPs);

      //sort push queue array
      this.patients = new Array<PatientInstance>();
      this.patients = this.masterPatients;
      this.patients$ = of(this.patients);

      this.patients.forEach(p => {
        let patientStudies = this.masterStudies.filter(st => st.patient_record_id == p.record_id);
        p.studies = patientStudies;
        p.studies$ = of(p.studies);
        p.studies.forEach((st) => {
          //update Study Progress
          let cMoveReq = this.ds.cMoveRequests.find(re => re.wado_study_record_id == st.record_id);
          if (cMoveReq) {
            st.updateProgress(cMoveReq);
          }
          let studySeries = this.masterSeries.filter(se => se.study_record_id == st.record_id);
          st.series = studySeries;
          st.series$ = of(st.series);
          st.series.forEach((se) => {
            let seriesSOPs = this.masterSOPs.filter(sop => sop.series_record_id == se.record_id);
            se.sops = seriesSOPs;
            se.sops$ = of(se.sops);
          });
        });
      });

      this.patients.forEach(p => {
        p.updateProgress(this.ds.cMoveRequests.length > 0);
      });
    } catch (err) {
      this.ms.handleClientError(err);
    }
  }

  parseAll(dcm_metadata: any): any {
    try {
      let patientInst = new PatientInstance(dcm_metadata.patient);
      let studyInst = new StudyInstance(dcm_metadata.study);
      let seriesInst = new SeriesInstance(dcm_metadata.series);
      let sopInst = new SOPInstance(dcm_metadata.sop);

      studyInst.patient = new PatientInstance(patientInst);
      seriesInst.study = new StudyInstance(studyInst);
      seriesInst.patient = new PatientInstance(patientInst);
      sopInst.series = new SeriesInstance(seriesInst);
      sopInst.study = new StudyInstance(studyInst);
      sopInst.patient = new PatientInstance(patientInst);

      this.parsePatient(patientInst);
      this.parseStudy(studyInst);
      this.parseSeries(seriesInst);
      this.parseSOP(sopInst);

      let patientExists = this.patients.find((p) => p.record_id == patientInst.record_id);
      if (!patientExists) {
        this.patients.push(patientInst);
        this.patients.sort((a: any, b: any) => {
          if (a.name < b.name) return -1;
          if (a.name > b.name) return 1;
          return 0;
        });
        this.patients$ = of(this.patients);
      }

      let patient = this.patients.find((p) => p.record_id == patientInst.record_id);
      let studyExists = patient.studies.find((s) => s.record_id == studyInst.record_id)
      if (!studyExists) {
        patient.studies.push(studyInst);
        patient.studies.sort((a: any, b: any) => {
          if (a.date < b.date) return -1;
          if (a.date > b.date) return 1;
          return 0;
        });
        patient.studies$ = of(patient.studies);
      }

      let study = patient.studies.find((s) => s.record_id == studyInst.record_id);
      let seriesExists = study.series.find((s) => s.record_id == seriesInst.record_id);

      if (!seriesExists) {
        study.series.push(seriesInst);
        study.series.sort((a: any, b: any) => {
          if (a.number < b.number) return -1;
          if (a.number > b.number) return 1;
          return 0;
        });
        study.series$ = of(study.series);
      }

      let series = study.series.find((s) => s.record_id == seriesInst.record_id);
      let sopExists = series.sops.find((s) => s.record_id == sopInst.record_id);
      if (!sopExists) {
        series.sops.push(sopInst);
        series.sops.sort((a: any, b: any) => {
          if (a.instance_number < b.instance_number) return -1;
          if (a.instance_number > b.instance_number) return 1;
          return 0;
        });
        series.sops$ = of(series.sops);
      }

    } catch (err) {
      this.ms.handleClientError(err);
    }
  }

  clearPatients(res): void {
    try {
      this.patients = new Array<PatientInstance>();
    } catch (err) {
      this.ms.handleClientError(err);
    }
  }

}
